
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDashboardData } from '@/api/dashboard';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { apiRequest } from '@/lib/api-client';
import { Link } from 'react-router-dom';
import { formatInAlbania } from '@/lib/date-utils';

interface AttendanceProps {
  selectedGym: string;
  className?: string;
}

export function Attendance({ selectedGym, className }: AttendanceProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [checkingOut, setCheckingOut] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardData', selectedGym],
    queryFn: () => getDashboardData(selectedGym),
    enabled: !!selectedGym,
  });

  // Filter for active check-ins (checkOut is null)
  const activeCheckIns =
    data?.attandanceCount?.filter((checkin) => checkin.checkOut === null) || [];

  const handleCheckOut = async (checkInId: string, userId: string) => {
    if (!selectedGym) {
      toast({
        title: 'Error',
        description: 'No gym selected',
        variant: 'destructive',
      });
      return;
    }

    setCheckingOut(checkInId);

    try {
      await apiRequest({
        method: 'POST',
        url: '/webhook/manual',
        data: {
          gymId: selectedGym,
          userId: userId,
          type: 'out',
        },
      });

      // Refresh dashboard data with the correct query key
      await queryClient.invalidateQueries({
        queryKey: ['dashboardData', selectedGym],
      });

      toast({
        title: 'Success',
        description: t('checkIn.checkOutSuccess'),
      });
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Error',
        description: t('checkIn.checkOutError'),
        variant: 'destructive',
      });
    } finally {
      setCheckingOut(null);
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('dashboard.attendance')}</CardTitle>
        <Button variant="outline" size="sm" asChild>
          <Link to="/check-in">{t('common.viewAll')}</Link>
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[200px]" />
                  <Skeleton className="h-4 w-[160px]" />
                </div>
              </div>
            ))}
          </div>
        ) : activeCheckIns.length > 0 ? (
          <div className="space-y-4">
            {activeCheckIns.map((checkin) => (
              <div
                key={checkin._id}
                className="flex items-center justify-between border-b pb-3"
              >
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    {checkin?.userId?.name?.charAt(0)}
                    {checkin?.userId?.surname?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {checkin?.userId?.name} {checkin?.userId?.surname}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatInAlbania(checkin.checkIn, 'DD-MM-YYYY HH:mm')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 px-2 py-1 rounded-full">
                    {t('checkIn.active')}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleCheckOut(checkin._id, checkin.userId._id)
                    }
                    disabled={checkingOut === checkin._id}
                  >
                    {checkingOut === checkin._id
                      ? t('checkIn.processing')
                      : t('checkIn.checkOut')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            {t('checkIn.noCheckIns')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
